import type { LucideIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/utils';
import { Button } from '../ui/button';

export type EmptyStateButtonVariant =
  | 'default'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'link';

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  icon?: LucideIcon | React.ComponentType<{ className?: string }>;
  variant?: EmptyStateButtonVariant;
  disabled?: boolean;
  className?: string;
}

export interface EmptyStateProps extends React.ComponentProps<'div'> {
  /** Centered visual indicator (Lucide icon or custom SVG). */
  icon?: LucideIcon | React.ComponentType<{ className?: string }>;
  /** Primary headline summarizing the state. */
  title: string;
  /** Secondary contextual explanation or guidance. */
  description?: React.ReactNode;
  /** Primary call-to-action button, action config, or custom element. */
  action?: EmptyStateAction | React.ReactNode;
  /** Optional secondary action button, action config, or custom element. */
  secondaryAction?: EmptyStateAction | React.ReactNode;
  /** Extra class names applied to the icon. */
  iconClassName?: string;
  /**
   * When true, wraps the empty state in a subtle card container
   * suitable for embedding inside tables, panels, or split views.
   */
  card?: boolean;
  /** Additional custom slot rendered beneath actions. */
  children?: React.ReactNode;
}

function renderAction(
  action: EmptyStateAction | React.ReactNode,
  defaultVariant: EmptyStateButtonVariant = 'default',
  key?: string
): React.ReactNode {
  if (!action) return null;
  if (React.isValidElement(action)) {
    return action;
  }
  const act = action as EmptyStateAction;
  const Icon = act.icon;
  return (
    <Button
      key={key}
      size="sm"
      variant={act.variant ?? defaultVariant}
      onClick={act.onClick}
      disabled={act.disabled}
      className={cn(Icon && 'gap-1.5', act.className)}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <span>{act.label}</span>
    </Button>
  );
}

/**
 * Standard, accessible empty state presentation for directories, tables, and views across all apps.
 * Follows the proven centered layout with subtle iconography, clear guidance, and direct action.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  iconClassName,
  card = false,
  className,
  children,
  ...props
}: EmptyStateProps) {
  const hasActions = Boolean(action || secondaryAction);

  return (
    <div
      data-slot="empty-state"
      className={cn(
        'text-center text-sm',
        card
          ? 'rounded-xl border border-border/60 bg-card/50 backdrop-blur-sm p-12 md:p-16'
          : 'py-16 max-w-sm mx-auto',
        className
      )}
      {...props}
    >
      <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
        {Icon && (
          <Icon
            className={cn('h-8 w-8 mx-auto mb-2 opacity-50 text-muted-foreground', iconClassName)}
          />
        )}
        <h3 className="font-semibold text-card-foreground text-base tracking-tight">{title}</h3>
        {description && (
          <p className="text-xs mt-1 text-muted-foreground leading-relaxed text-balance">
            {description}
          </p>
        )}

        {hasActions && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            {renderAction(action, 'default', 'primary-action')}
            {renderAction(secondaryAction, 'outline', 'secondary-action')}
          </div>
        )}

        {children && <div className="mt-4 w-full">{children}</div>}
      </div>
    </div>
  );
}
